import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertCenter = () => {
  const [alerts, setAlerts] = useState([
    {
      id: 1,
      type: 'critical',
      title: 'Payment Gateway Issue',
      message: 'Multiple payment failures detected in the last hour. Customer checkout may be affected.',
      timestamp: new Date(Date.now() - 600000),
      action: 'Check Payment Settings',
      dismissed: false
    },
    {
      id: 2,
      type: 'warning',
      title: 'Inventory Shortage',
      message: '15 products are out of stock and 8 products have low inventory levels.',
      timestamp: new Date(Date.now() - 1800000),
      action: 'Review Inventory',
      dismissed: false
    },
    {
      id: 3,
      type: 'info',
      title: 'Vendor Compliance Review',
      message: '3 vendors require updated documentation for compliance verification.',
      timestamp: new Date(Date.now() - 3600000),
      action: 'Review Vendors',
      dismissed: false
    },
    {
      id: 4,
      type: 'success',
      title: 'System Backup Complete',
      message: 'Daily system backup completed successfully at 2:00 AM.',
      timestamp: new Date(Date.now() - 7200000),
      action: 'View Backup Log',
      dismissed: false
    }
  ]);

  const getAlertConfig = (type) => {
    const configs = {
      critical: {
        icon: 'AlertCircle',
        bgColor: 'bg-error/10',
        borderColor: 'border-error/20',
        iconColor: 'text-error',
        textColor: 'text-error'
      },
      warning: {
        icon: 'AlertTriangle',
        bgColor: 'bg-warning/10',
        borderColor: 'border-warning/20',
        iconColor: 'text-warning',
        textColor: 'text-warning'
      },
      info: {
        icon: 'Info',
        bgColor: 'bg-primary/10',
        borderColor: 'border-primary/20',
        iconColor: 'text-primary',
        textColor: 'text-primary'
      },
      success: {
        icon: 'CheckCircle',
        bgColor: 'bg-success/10',
        borderColor: 'border-success/20',
        iconColor: 'text-success',
        textColor: 'text-success'
      }
    };
    return configs?.[type] || configs?.info;
  };

  const dismissAlert = (alertId) => {
    setAlerts(prev => prev?.map(alert => 
      alert?.id === alertId ? { ...alert, dismissed: true } : alert
    ));
  };

  const handleAlertAction = (alertId, action) => {
    console.log(`Execute action: ${action} for alert: ${alertId}`);
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return timestamp?.toLocaleDateString();
    }
  };

  const activeAlerts = alerts?.filter(alert => !alert?.dismissed);
  const criticalCount = activeAlerts?.filter(alert => alert?.type === 'critical')?.length;
  const warningCount = activeAlerts?.filter(alert => alert?.type === 'warning')?.length;

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-heading font-semibold text-foreground">Alert Center</h3>
          {(criticalCount > 0 || warningCount > 0) && (
            <div className="flex items-center space-x-2">
              {criticalCount > 0 && (
                <span className="bg-error text-error-foreground text-xs px-3 py-1.5 rounded-full flex flex-col items-center justify-center">
                  <span className="font-semibold">{criticalCount}</span>
                  <span className="text-[10px]">Critical</span>
                </span>
              )}
              {warningCount > 0 && (
                <span className="bg-warning text-warning-foreground text-xs px-3 py-1.5 rounded-full flex flex-col items-center justify-center">
                  <span className="font-semibold">{warningCount}</span>
                  <span className="text-[10px]">Warning</span>
                </span>
              )}
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" iconName="Settings" iconPosition="left">
          Configure
        </Button>
      </div>
      {activeAlerts?.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={24} className="text-success" />
          </div>
          <h4 className="text-sm font-medium text-foreground mb-2">All Clear!</h4>
          <p className="text-xs text-muted-foreground">No active alerts requiring attention</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          {activeAlerts?.map((alert) => {
            const config = getAlertConfig(alert?.type);
            return (
              <div
                key={alert?.id}
                className={`p-3.5 rounded-lg border ${config?.bgColor} ${config?.borderColor}`}
              >
                <div className="flex items-start space-x-3">
                  <Icon name={config?.icon} size={18} className={`${config?.iconColor} flex-shrink-0 mt-0.5`} />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h4 className={`text-sm font-medium ${config?.textColor} break-words`}>
                          {alert?.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-1 break-words leading-relaxed">
                          {alert?.message}
                        </p>
                        <span className="text-xs text-muted-foreground mt-1 inline-block">
                          {formatTimeAgo(alert?.timestamp)}
                        </span>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => dismissAlert(alert?.id)}
                        className="flex-shrink-0 h-6 w-6"
                      >
                        <Icon name="X" size={14} />
                      </Button>
                    </div>
                    
                    <div className="flex items-center flex-wrap gap-2 mt-2.5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAlertAction(alert?.id, alert?.action)}
                        className="text-xs h-7 px-3"
                      >
                        {alert?.action}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {activeAlerts?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <Button variant="outline" fullWidth iconName="Archive" iconPosition="left">
            View Alert History
          </Button>
        </div>
      )}
    </div>
  );
};

export default AlertCenter;